/**
 * ThemeContext — manages light / dark / system theme.
 *
 * - Persists preference to localStorage under key 'fv-theme'
 * - Applies data-theme="light"|"dark" to <html> for CSS to react to
 * - "system" (default) defers to OS preference via prefers-color-scheme
 *
 * Usage:
 *   const { theme, resolvedTheme, toggleTheme } = useTheme();
 *   theme         → 'light' | 'dark' | 'system'
 *   resolvedTheme → 'light' | 'dark'  (always concrete)
 *   toggleTheme() → cycles light ↔ dark (clears system preference)
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeContext = createContext({
  theme: 'system',
  resolvedTheme: 'light',
  toggleTheme: () => {},
  setTheme: () => {},
});

/** Returns the OS colour-scheme preference at call-time. */
function getSystemTheme() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    return localStorage.getItem('fv-theme') || 'system';
  });

  const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;

  // Apply data-theme to <html> whenever resolved theme changes
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', theme);
    }
  }, [theme]);

  // Keep in sync if OS preference changes while user is on 'system'
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      // Force re-render by refreshing state (theme itself doesn't change)
      setThemeState(t => t);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    localStorage.setItem('fv-theme', next);
  }, []);

  const toggleTheme = useCallback(() => {
    // Toggle between light ↔ dark (removes system preference)
    const next = resolvedTheme === 'dark' ? 'light' : 'dark';
    setTheme(next);
  }, [resolvedTheme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
